"use client";

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";
import {
  MaximumMortgageWizardResult,
  type MaximumMortgageWizardResultProps,
  type MaximumMortgageWizardResultCTAKey,
  type MaximumMortgageCalculationResult,
  type MortgageInterestRate,
  type InterestRatesSimpleHandle,
  type InterestRatesSimpleFilterValues,
  MaximumMortgageWizardResultLoadingScreen,
  MaximumMortgageWizardResultErrorScreen,
  parseSearchParamsToMaximumMortgageWizardValues,
  type MortgageInterestRatePeriod,
} from "@huub-web/shared";
import {
  Button,
  Flex,
  Spacing,
  Stack,
  useActions,
  useDebug,
  useDelayedState,
  type FormFieldSchema,
  type UIAction,
  type UINavigateAction,
} from "@huub-web/ui";
import { addParamsToURL, getSlug, isWindowDefined } from "@huub-web/utils";

import { interestRatesSimpleImageStyle } from "@huub-web/shared/components/InterestRatesSimple/InterestRatesSimple.css";

import {
  useTemplates,
  useInputUpdate,
  useAgentFormAwareness,
} from "../../hooks";
import { PageContentContext } from "../../contexts/PageContentContext";

export type MaximumMortgageWizardResultTemplateProps = {
  /**
   * Unique identifier for this template instance
   */
  id: string;
  interestRatesEndpoint?: string;
  calculateEndpoint: string;
  CTAs?: Record<MaximumMortgageWizardResultCTAKey, UIAction>;
  /**
   * Named UIActions for additional interactions
   */
  actions?: {
    /**
     * Bank click action (template appends bank slug to navigation context)
     */
    bank?: UINavigateAction;
  };
  /**
   * Initial NHG toggle state for the interest rates card.
   * Defaults to true ("Met") when not provided.
   */
  defaultInterestRateNhg?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Fetches and displays mortgage calculation results.
 * Supports dual-mode loading: by saved calculation UUID (`?id=<uuid>`)
 * or by legacy query-param values. Provides interest rate filtering and CTA actions.
 */
export const MaximumMortgageWizardResultTemplate: FC<
  MaximumMortgageWizardResultTemplateProps
> = ({
  id,
  interestRatesEndpoint,
  calculateEndpoint,
  CTAs,
  actions,
  defaultInterestRateNhg,
  ref,
}) => {
  const { $get, registerUIContext, deregisterUIContext } = useTemplates();
  const { handleAction } = useActions();
  const { log } = useDebug();
  const { t } = useTranslation();
  const { registerContent, deregisterContent } = useContext(PageContentContext);

  const interestRatesFilterRef = useRef<InterestRatesSimpleHandle>(null);

  const nhgTransform = useCallback(
    (fieldName: string, value: unknown) => ({
      fieldName,
      value: fieldName === "nhg" ? value === "true" || value === true : value,
    }),
    [],
  );

  useInputUpdate({
    prefix: `${id}__interest_rates__`,
    debugLabel: "MaximumMortgageWizardResultTemplate",
    onUpdate: useCallback((updates: Record<string, unknown>) => {
      interestRatesFilterRef.current?.updateFilterValues(
        updates as Partial<InterestRatesSimpleFilterValues>,
      );
    }, []),
    transform: nhgTransform,
  });

  const [result, setResult] = useState<MaximumMortgageCalculationResult | null>(
    null,
  );
  const [loading, , setImmediateLoading] = useDelayedState(true, 1000);
  const [error, setError] = useState<boolean>(false);
  /**
   * NHG default is driven by the calculation result (`nhgEligible`).
   * Falls back to the prop value when provided, otherwise starts as false
   * ("Zonder") until the result is loaded and nhgEligible is known.
   */
  const [resolvedNhg, setResolvedNhg] = useState<boolean>(
    defaultInterestRateNhg ?? false,
  );

  useEffect(() => {
    if (!result || !CTAs) return;
    const rt = result.resultType;
    const ctaLabels: [string, string][] = [
      ["chanceCard", t(`maxMortgageWizard.result.${rt}.cta.chanceCard`)],
      ["yourSituation", t(`maxMortgageWizard.result.${rt}.cta.yourSituation`)],
      [
        "neededSituation",
        t(`maxMortgageWizard.result.${rt}.cta.neededSituation`),
      ],
      [
        "costCalculation",
        t(`maxMortgageWizard.result.${rt}.cta.costCalculation`),
      ],
      ["interestRate", t(`maxMortgageWizard.result.${rt}.cta.interestRate`)],
    ];
    const ids: string[] = [];
    for (const [key, label] of ctaLabels) {
      if (!CTAs[key as MaximumMortgageWizardResultCTAKey]) continue;
      const contentId = `max-mortgage-cta-${key}`;
      registerContent(contentId, {
        type: "cta",
        label,
        action: CTAs[key as MaximumMortgageWizardResultCTAKey],
      });
      ids.push(contentId);
    }
    return () => {
      for (const cid of ids) deregisterContent(cid);
    };
  }, [result, CTAs, t, registerContent, deregisterContent]);

  // Extract search params from URL for templates that need them
  // Memoized to prevent infinite loop in useEffect
  const searchParams = useMemo(() => {
    if (!isWindowDefined()) {
      return {};
    }
    const params = new URLSearchParams(window.location.search);
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }, []);

  const getResult = useCallback(async () => {
    if (!calculateEndpoint) {
      log(
        "red",
        "MaximumMortgageWizardResultTemplate",
        "No calculate endpoint provided",
      );
      setError(true);
      setImmediateLoading(false);
      return;
    }

    log(
      "purple",
      "MaximumMortgageWizardResultTemplate",
      "getResult",
      calculateEndpoint,
    );

    setImmediateLoading(true);
    setError(false);

    try {
      const idParam = searchParams.id;

      const params = idParam
        ? { item_id: idParam }
        : Object.fromEntries(
            Object.entries(
              parseSearchParamsToMaximumMortgageWizardValues(searchParams),
            )
              .filter(([, value]) => value !== undefined)
              .map(([key, value]) => [key, String(value)]),
          );

      const response = await $get(calculateEndpoint, { params });

      if (response?.data?.success && response?.data?.result) {
        const calculationResult = response.data
          .result as MaximumMortgageCalculationResult;
        setResult(calculationResult);
        // Use nhgEligible from the result as the authoritative NHG default.
        // Only fall back to the prop when the field is absent (older API responses).
        if (calculationResult.nhgEligible !== undefined) {
          setResolvedNhg(calculationResult.nhgEligible);
        }
      } else {
        log(
          "red",
          "MaximumMortgageWizardResultTemplate",
          "getResult: API returned non-success",
          response,
        );
        setError(true);
      }
    } catch (err) {
      log("red", "MaximumMortgageWizardResultTemplate", "getResult error", err);
      setError(true);
    } finally {
      setImmediateLoading(false);
    }
    // setImmediateLoading is intentionally omitted from deps — it is an inline
    // function from useDelayedState (not wrapped in useCallback), so it gets a
    // new reference every render. Including it would cause an infinite fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateEndpoint, searchParams, $get, log]);

  useEffect(() => {
    getResult();
  }, [getResult]);

  const getInterestRates: MaximumMortgageWizardResultProps["getInterestRates"] =
    useCallback(
      async (period: MortgageInterestRatePeriod, nhg: boolean) => {
        if (!interestRatesEndpoint) {
          log(
            "red",
            "MaximumMortgageWizardResultTemplate",
            "No interest rates endpoint provided",
          );
          return [];
        }

        try {
          const data = await $get(
            addParamsToURL(interestRatesEndpoint, { period, nhg }),
          );
          log(
            "purple",
            "MaximumMortgageWizardResultTemplate",
            "getInterestRates",
            data,
          );
          if (!data?.data?.rates) {
            log(
              "red",
              "MaximumMortgageWizardResultTemplate",
              "Unexpected interest rates response shape",
              data,
            );
          }
          return data?.data?.rates ?? [];
        } catch (err) {
          log(
            "red",
            "MaximumMortgageWizardResultTemplate",
            "getInterestRates error",
            err,
          );
          return [];
        }
      },
      [interestRatesEndpoint, $get, log],
    );

  const bankAction = actions?.bank;

  /**
   * Wraps the bank logo image in a clickable Button that navigates
   * to the provider's rente vergelijken detail page via UIAction.
   */
  const getBankImage = useCallback(
    (rate: MortgageInterestRate) => {
      if (!bankAction) {
        return (
          <img
            src={rate.image}
            alt={rate.name}
            className={interestRatesSimpleImageStyle}
          />
        );
      }

      const slug = rate.slug ?? getSlug(rate.name);
      const baseContext = bankAction.payload?.context ?? [];

      return (
        <Button
          onClick={() =>
            handleAction({
              type: "navigate",
              payload: {
                context: [...baseContext, slug],
                ...(bankAction.payload?.params && {
                  params: bankAction.payload.params,
                }),
              },
            } satisfies UIAction)
          }
          variant="opaqueCurrentColor"
          tracking="maximum_mortgage_wizard_result_bank_logo"
        >
          <img
            src={rate.image}
            alt={rate.name}
            className={interestRatesSimpleImageStyle}
          />
        </Button>
      );
    },
    [bankAction, handleAction],
  );

  const [currentFilters, setCurrentFilters] = useState<{
    rentevastePeriod: string;
    nhg: string;
  }>({ rentevastePeriod: "20", nhg: "true" });

  const RESULT_FILTER_SCHEMA: Omit<FormFieldSchema, "value" | "errors">[] =
    useMemo(
      () => [
        {
          name: "rentevastePeriod",
          label: "Rentevaste periode (jaren)",
          type: "select",
          options: [
            { value: "5", label: "5 jaar" },
            { value: "10", label: "10 jaar" },
            { value: "20", label: "20 jaar" },
            { value: "30", label: "30 jaar" },
          ],
        },
        {
          name: "nhg",
          label: "NHG (Nationale Hypotheek Garantie)",
          type: "select",
          options: [
            { value: "true", label: "Ja" },
            { value: "false", label: "Nee" },
          ],
        },
      ],
      [],
    );

  const fieldSchemaWithValues = useMemo<FormFieldSchema[]>(
    () =>
      RESULT_FILTER_SCHEMA.map((f) => ({
        ...f,
        value: currentFilters[f.name as keyof typeof currentFilters] ?? null,
        errors: [],
      })),
    [RESULT_FILTER_SCHEMA, currentFilters],
  );

  useAgentFormAwareness({
    formId: `${id}__interest_rates`,
    formType: "filter",
    fieldSchema: fieldSchemaWithValues,
  });

  /**
   * Register the calculation result with the agent UI context so the
   * sidebar chat can see what the user sees on the result page.
   */
  useEffect(() => {
    if (!result) return;
    const sliceId = `${id}__calculation_result`;
    const purpose = result.values?.purpose ?? "finance";
    const isRenovation = purpose === "renovate";
    const isRefinance = purpose === "refinance";
    const fields = [
      {
        fieldKey: "purpose",
        label: "Doel",
        type: "text",
        value: purpose,
      },
      {
        fieldKey: "maximumLoan",
        label: "Maximale hypotheek op basis van inkomen",
        type: "currency",
        value: result.maximumLoan,
      },
      {
        fieldKey: "interestRate",
        label: "Rente",
        type: "percentage",
        value: result.interestRate,
      },
      {
        fieldKey: "interestYears",
        label: "Rentevaste periode",
        type: "number",
        value: result.interestYears,
      },
      {
        fieldKey: "monthlyCosts",
        label: "Maandlasten",
        type: "currency",
        value: result.monthlyCosts,
      },
      {
        fieldKey: "neededLoan",
        label: isRenovation
          ? "Beschikbaar voor ophoging"
          : isRefinance
            ? "Benodigde financiering"
            : "Benodigde hypotheek",
        type: "currency",
        value: result.neededLoan,
      },
      ...(isRenovation || isRefinance
        ? []
        : [
            {
              fieldKey: "askingPrice",
              label: "Vraagprijs",
              type: "currency",
              value: result.askingPrice,
            },
          ]),
      {
        fieldKey: "fundablePercentage",
        label: "Financierbaarheid",
        type: "percentage",
        value: result.fundablePercentage,
      },
      {
        fieldKey: "resultType",
        label: "Resultaat",
        type: "text",
        value: result.resultType,
      },
    ];
    registerUIContext?.(sliceId, {
      type: "form",
      formType: "filter",
      formContext: { formId: sliceId, fields },
    });
    return () => {
      deregisterUIContext?.(sliceId);
    };
  }, [id, result, registerUIContext, deregisterUIContext]);

  const handleFiltersChange = useCallback(
    (filters: { rentevastePeriod: string; nhg: string }) => {
      setCurrentFilters(filters);
    },
    [],
  );

  return (
    <Stack>
      {loading && (
        <Flex direction="column" gap="xl">
          <Spacing />
          <MaximumMortgageWizardResultLoadingScreen container="default" />
        </Flex>
      )}
      {!loading && error && (
        <MaximumMortgageWizardResultErrorScreen container="default" />
      )}
      {!loading && !error && result && (
        <Flex direction="column" gap="xl">
          <Spacing />
          <MaximumMortgageWizardResult
            ref={ref}
            container="default"
            result={result}
            getInterestRates={
              interestRatesEndpoint ? getInterestRates : undefined
            }
            getBankImage={interestRatesEndpoint ? getBankImage : undefined}
            onInterestRateFiltersChange={handleFiltersChange}
            interestRatesFilterRef={interestRatesFilterRef}
            defaultInterestRateNhg={resolvedNhg}
            CTAs={{
              chanceCard: {
                onClick: () =>
                  CTAs?.chanceCard && handleAction(CTAs?.chanceCard),
                tracking: "maximum_mortgage_wizard_result_chance_card_cta",
              },
              advisor: {
                onClick: () => CTAs?.advisor && handleAction(CTAs?.advisor),
                tracking: "maximum_mortgage_wizard_result_advisor_cta",
              },
              continue: {
                onClick: () => CTAs?.continue && handleAction(CTAs?.continue),
                tracking: "maximum_mortgage_wizard_result_continue_cta",
              },
              costCalculation: {
                onClick: () =>
                  CTAs?.costCalculation && handleAction(CTAs?.costCalculation),
                tracking: "maximum_mortgage_wizard_result_cost_calculation_cta",
              },
              yourSituation: {
                onClick: () =>
                  CTAs?.yourSituation && handleAction(CTAs?.yourSituation),
                tracking: "maximum_mortgage_wizard_result_your_situation_cta",
              },
              neededSituation: {
                onClick: () =>
                  CTAs?.neededSituation && handleAction(CTAs?.neededSituation),
                tracking: "maximum_mortgage_wizard_result_needed_situation_cta",
              },
              interestRate: {
                onClick: () =>
                  CTAs?.interestRate && handleAction(CTAs?.interestRate),
                tracking: "maximum_mortgage_wizard_result_interest_rate_cta",
              },
              analysis: {
                onClick: () => CTAs?.analysis && handleAction(CTAs?.analysis),
                tracking: "maximum_mortgage_wizard_result_analysis_cta",
              },
            }}
          />
        </Flex>
      )}
    </Stack>
  );
};
